import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal as EPModal, Copy, Flex, Input, Box, Button, Image, ErrorsWrapper } from '@elparking/components'
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl'
import { phone } from 'commons/js/util/validators'
import { useApi } from '@elparking/utils'
import clickToCall from 'commons/js/api/rentalCar/clickToCall'
import {
    RENTAL_CAR_CLICK_TO_CALL_REGISTER,
    RENTAL_CAR_CLICK_TO_CALL_INFO,
    RENTAL_CAR_CLICK_TO_CALL_RECOVER_CART,
    RENTAL_CAR_CLICK_TO_CALL_NO_RESULTS,
} from 'commons/js/constants'
import moment from 'moment'
import ClickToCallGDPRModal from './ClickToCallGDPRModal'

const ClickToCallImage = {
    [RENTAL_CAR_CLICK_TO_CALL_REGISTER]: '/images/rental-car/click-to-call-register.svg',
    [RENTAL_CAR_CLICK_TO_CALL_INFO]: '/images/rental-car/click-to-call-info.svg',
    [RENTAL_CAR_CLICK_TO_CALL_RECOVER_CART]: '/images/rental-car/click-to-call-info.svg',
    [RENTAL_CAR_CLICK_TO_CALL_NO_RESULTS]: '/images/rental-car/no-cards.svg',
}

const ClickToCallTitle = {
    [RENTAL_CAR_CLICK_TO_CALL_REGISTER]: <FormattedMessage defaultMessage='¿Aún no tienes cuenta en MutuaMás?'/>,
    [RENTAL_CAR_CLICK_TO_CALL_INFO]: <FormattedMessage defaultMessage='¿Necesitas más información?'/>,
    [RENTAL_CAR_CLICK_TO_CALL_RECOVER_CART]: <FormattedMessage defaultMessage='¿Te está costando encontrar lo que buscas?' />,
    [RENTAL_CAR_CLICK_TO_CALL_NO_RESULTS]: <FormattedMessage defaultMessage='Vaya, no hay resultados para tu búsqueda...'/>,
}

const ClickToCallDescription = {
    [RENTAL_CAR_CLICK_TO_CALL_REGISTER]: <>
        <Copy fontSize='large' fontWeight='regular' padding='0'><FormattedMessage defaultMessage='Indícanos tu teléfono y te llamaremos lo antes posible para ofrecerte el coche de alquiler que más se ajuste a tus necesidades, al mejor precio'/></Copy>
    </>,
    [RENTAL_CAR_CLICK_TO_CALL_INFO]: <FormattedMessage defaultMessage='Un experto puede asesorarte gratuitamente para ayudarte con tu búsqueda. Te llamaremos lo antes posible para ofrecerte el coche de alquiler que más se ajuste a tus necesidades y al mejor precio.'/>,
    [RENTAL_CAR_CLICK_TO_CALL_RECOVER_CART]: <FormattedMessage defaultMessage='Un experto puede asesorarte gratuitamente para ayudarte con tu búsqueda. Te llamaremos lo antes posible para ofrecerte el coche de alquiler que más se ajuste a tus necesidades y al mejor precio.' />,
    [RENTAL_CAR_CLICK_TO_CALL_NO_RESULTS]: <Copy fontSize={['small', 'medium']} align='center' fontWeight='book' padding='0.5em 0'><FormattedMessage defaultMessage='{helpText} Un experto puede asesorarte gratuitamente para ayudarte con tu búsqueda y ofrecerte el coche de alquiler que más se ajuste a tus necesidades y al mejor precio.' values={{
        helpText: <Copy as='span' fontWeight='bold' fontSize={['small', 'medium']}><FormattedMessage defaultMessage='¿Te ayudamos?' /> </Copy>,
    }}/></Copy>,
}

const validatePhone = phone()
const messages = defineMessages({
    placeholder: {
        defaultMessage: 'Introduce tu número de teléfono',
    },
    label: {
        defaultMessage: 'Número móvil',
    },
})

const ClickToCallModal = ({ isShown, onClose, step, intl, sendPhoneNumberCall, modalType, zIndex, showExtraC2CInfo, onLoad, onSuccessPhoneNumberCallSent }) => {
    const [phoneNumber, setPhoneNumber] = useState('')
    const [isShownGDPR, setShownGDPR] = useState(false)
    const [validPhoneNumber, setValidPhoneNumber] = useState(false)
    const [isSubmit, setIsSubmit] = useState(false)

    useEffect(() => {
        if (isShown) onLoad()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShown])

    const onSubmit = () => {
        setIsSubmit(true)
        validPhoneNumber && requestPhoneNumberCall({
            idPersona: null,
            paso: step,
            precio: 'SI',
            subOrigen: 'C2C',
            origen: 'Digital',
            canal: 'Directo',
            ramo: '99',
            fechaInteraccion: moment().valueOf(),
            documento: null,
            apellidos: null,
            nombre: 'SIN NOMBRE',
            idSolicitud: null,
            idCorrelacion: null,
            idProveedor: null,
            varFormulario: [
                {
                    tipoX: 'string',
                    valorX: 'no ap',
                    varX: 'logado_mutua',
                },
                {
                    tipoX: 'string',
                    valorX: 'rental_car_' + (new Date()).getMilliseconds(),
                    varX: 'myIdLeadMM',
                },
                {
                    tipoX: 'string',
                    valorX: 'myIdLeadMM',
                    varX: 'idLeadMM',
                },
                {
                    tipoX: 'string',
                    valorX: 'MM_SERVICIOS_RENTACAR_COTIZADOR',
                    varX: 'tipoCampaign',
                },
                {
                    varX: 'urlOrigin',
                    valorX: window.location.origin + window.location.pathname,
                    tipoX: 'STRING',
                },
            ],
            canalComunicacion: [
                {
                    tipo: 'TFNO',
                    valor: phoneNumber,
                },
            ],
        })
    }

    const onPhoneNumberChanged = newValue => {
        setValidPhoneNumber(validatePhone(newValue))
        setPhoneNumber(newValue)
        phoneNumberCallError && reset()
    }

    const {
        response: { result: phoneNumberCallSent, error: phoneNumberCallError },
        loading: phoneNumberCallLoading,
        sendRequest: requestPhoneNumberCall,
        resetResponse: reset,
    } = useApi(sendPhoneNumberCall)

    useEffect(() => {
        if (phoneNumberCallSent) {
            onSuccessPhoneNumberCallSent()
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneNumberCallSent])

    const close = useCallback(() => {
        reset()
        onClose()
    }, [onClose, reset])

    const hasInputError = (isSubmit && !validPhoneNumber) || phoneNumberCallError
    return <>
        <EPModal dataTest='click-to-call-modal' isShown={isShown} onClose={close} width='690px' fullScreenMobile={false} label='' showModalTitle={false} zIndex={zIndex}>
            { phoneNumberCallSent ? <Flex mt={['0px', '3em']} flexDirection='column' dataTest='help-modal-number-sent'>
                <Box width='100%' display='block' textAlign='center'>
                    <Image src='/images/rental-car/click-to-call-success.svg' width='150px' alignSelf='center' alt='ok' />
                </Box>
                <Copy as='h1' fontSize={['xLarge', 'xxLarge']} fontWeight='medium' textAlign='center' color='accent_dark' padding-top='1em'>
                    <FormattedMessage defaultMessage='¡Tenemos tu número!' />
                </Copy>
                <Copy as='h2' fontSize='large' fontWeight='regular' mb='1em' textAlign='center'>
                    <FormattedMessage defaultMessage='En breve recibirás la llamada de un experto para continuar con tu reserva. Esta llamada es gratuita y no tendrá ningún coste para ti ¡Gracias!' />

                </Copy>
            </Flex>
            : <Flex mt={['0px', '3em']} flexDirection='column'>
                <Box width='100%' textAlign='center'>
                    <Image src={ClickToCallImage[modalType]} width='150px' alignSelf='center' alt='help' />
                </Box>
                <Copy as='h1' fontSize={['xLarge', 'xxLarge']} fontWeight='medium' textAlign='center' color='accent_dark90' padding-top='1em'>
                    {ClickToCallTitle[modalType]}
                </Copy>
                <Copy as='h2' fontSize='large' fontWeight='regular' mb='1em' textAlign='center'>
                    {ClickToCallDescription[modalType]}
                </Copy>
                <Box width={['100%', '50%']} alignSelf='center' mb='1em'>
                    <Input
                      value={phoneNumber}
                      onChange={onPhoneNumberChanged}
                      label={intl.formatMessage(messages.label)}
                      placeholder={intl.formatMessage(messages.placeholder)}
                      valid={validPhoneNumber}
                      focusColor={hasInputError ? 'error' : 'accent_dark70'}
                      />
                    {isSubmit && !validPhoneNumber &&
                    <ErrorsWrapper dataTest='help-modal-wrong-phone-format-message'>
                        <Copy textAlign='left' fontWeight='regular' color='error' size='xSmall' padding='0 0.5em'>
                            <FormattedMessage defaultMessage='Introduce un número de teléfono válido.' />
                        </Copy>
                    </ErrorsWrapper>
                    }
                    {phoneNumberCallError &&
                    <ErrorsWrapper>
                        <Copy textAlign='left' fontWeight='regular' color='error' size='xSmall' padding='0 0.5em'>
                            <FormattedMessage defaultMessage='Ha ocurrido un error al intentar enviar su número de teléfono. Por favor, inténtalo de nuevo en unos momentos.' />
                        </Copy>
                    </ErrorsWrapper>
                    }
                </Box>
                {showExtraC2CInfo && <Copy as='p' fontSize={['small', 'medium']} align='center' fontWeight='book' padding='0.5em 0'><FormattedMessage defaultMessage='Te llamaremos lo antes posible o, si lo prefieres, puedes volver a intentarlo con una nueva selección' /> </Copy>}
                <Copy as='p' fontSize='xxSmall' color='gray70' fontWeight='regular' textAlign='center'>
                    <FormattedMessage defaultMessage='La llamada es gratuita y Autoclub, como responsable del tratamiento, únicamente utilizarán tu número para atender tu consulta sobre contratación de vehículos. Puedes ejercer tus derechos de protección de datos y ampliar la información sobre nuestra política de privacidad {gdpr}.' values={{
                        gdpr: <Button.unstyled textTransform='none' fontSize='xxSmall' dataTest='help-modal-gdpr-button' fontColor='main' onClick={() => setShownGDPR(true)}><FormattedMessage defaultMessage='aquí'/></Button.unstyled>,
                    }}/>
                </Copy>
                <Flex width='100%' justifyContent='center' alignItems='center' mt='1em' mb={['0em', '1em']}>
                    <Button dataTest='help-modal-submit-button' onClick={onSubmit} fontWeight='bold' disabled={phoneNumberCallLoading} mb={['1em', '0em']} width={['100%', '50%']}> <FormattedMessage defaultMessage='Te llamamos'/></Button>
                </Flex>
            </Flex>}
        </EPModal>
        <ClickToCallGDPRModal isShown={isShownGDPR} onClose={() => setShownGDPR(false)} />
    </>
}

ClickToCallModal.propTypes = {
    isShown: PropTypes.bool,
    onClose: PropTypes.func,
    intl: PropTypes.object,
    sendPhoneNumberCall: PropTypes.func,
    modalType: PropTypes.string,
    step: PropTypes.any,
    zIndex: PropTypes.number,
    showExtraC2CInfo: PropTypes.bool,
    onLoad: PropTypes.func,
    onSuccessPhoneNumberCallSent: PropTypes.func,
}

ClickToCallModal.defaultProps = {
    sendPhoneNumberCall: clickToCall,
    modalType: RENTAL_CAR_CLICK_TO_CALL_REGISTER,
    step: 'P0',
    showExtraC2CInfo: false,
    onLoad: () => {},
    onSuccessPhoneNumberCallSent: () => { },
}
export default injectIntl(ClickToCallModal)
